import { useEffect, useRef, useCallback } from 'react';
import { flagActivity } from '../api/candidate.api';

/**
 * useAntiCheat
 * Binds all anti-cheat event listeners during exam.
 * Returns { tabSwitchCount }
 *
 * @param {object} options
 * @param {number}   options.sessionId
 * @param {number}   options.maxTabSwitches
 * @param {Function} options.onTabSwitch        - called with (count, remaining)
 * @param {Function} options.onAutoSubmit        - called when tab limit reached
 * @param {boolean}  options.active              - only bind when true
 */
const useAntiCheat = ({ sessionId, maxTabSwitches = 3, onTabSwitch, onAutoSubmit, active }) => {
  const tabCountRef = useRef(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  const flag = useCallback(
    (type, detail = '') => {
      if (!sessionId) return;
      flagActivity(sessionId, { type, detail }).catch(() => {});
    },
    [sessionId]
  );

  // ── Tab Switch / Visibility ──────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    const handleVisibility = () => {
      if (!activeRef.current) return;
      if (document.hidden) {
        tabCountRef.current += 1;
        const count = tabCountRef.current;
        flagActivity(sessionId, { type: 'TAB_SWITCH', detail: `count:${count}` }).catch(() => {});

        if (count >= maxTabSwitches) {
          onAutoSubmit?.('tab_limit');
        } else {
          onTabSwitch?.(count, maxTabSwitches - count);
        }
      }
    };

    const handleBlur = () => {
      if (!activeRef.current || document.hidden) return; // avoid double-counting with visibilitychange
      flag('WINDOW_BLUR');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
    };
  }, [active, sessionId, maxTabSwitches, onTabSwitch, onAutoSubmit, flag]);

  // ── Copy / Paste / Cut / Right-click / Selection ─────────────────────────
  useEffect(() => {
    if (!active) return;

    const prevent = (e) => e.preventDefault();
    const flagAndPrevent = (type) => (e) => { e.preventDefault(); flag(type); };

    document.addEventListener('copy', flagAndPrevent('COPY_ATTEMPT'));
    document.addEventListener('paste', prevent);
    document.addEventListener('cut', prevent);
    document.addEventListener('contextmenu', prevent);
    document.addEventListener('selectstart', prevent);

    return () => {
      document.removeEventListener('copy', flagAndPrevent('COPY_ATTEMPT'));
      document.removeEventListener('paste', prevent);
      document.removeEventListener('cut', prevent);
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('selectstart', prevent);
    };
  }, [active, flag]);

  // ── Keyboard Shortcut Blocking ────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    const handleKeydown = (e) => {
      const key = e.key.toLowerCase();
      // Block Ctrl+C/V/X/A/P/S/U
      if (e.ctrlKey && ['c', 'v', 'x', 'a', 'p', 's', 'u'].includes(key)) {
        e.preventDefault();
      }
      // Block F12 and DevTools shortcuts
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(key))
      ) {
        e.preventDefault();
        flag('DEVTOOLS_OPEN');
      }
      // Block PrintScreen
      if (e.key === 'PrintScreen') e.preventDefault();
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [active, flag]);

  // ── Page Unload (sendBeacon) ──────────────────────────────────────────────
  useEffect(() => {
    if (!active || !sessionId) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your exam is in progress. Are you sure you want to leave?';
      // sendBeacon survives page unload
      const body = JSON.stringify({ type: 'PAGE_UNLOAD' });
      navigator.sendBeacon(
        `/api/candidate/sessions/${sessionId}/flag-activity`,
        new Blob([body], { type: 'application/json' })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [active, sessionId]);

  return { tabSwitchCount: tabCountRef.current };
};

export default useAntiCheat;
